import { AxiosInstance } from 'axios'
import { PaginatedResponse } from '../base.type'
import {
    Approval,
    ApprovalTemplate,
    ApproveRequestBody,
    CreateApprovalTemplateDto,
    ListApprovalsParams,
    RejectRequestBody,
    UpdateApprovalTemplateDto,
} from './approval.type'

export class ApprovalApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all approvals with pagination and filters
     */
    async list(params?: ListApprovalsParams): Promise<PaginatedResponse<ApprovalTemplate>> {
        const response = await this.client.get<PaginatedResponse<ApprovalTemplate>>('/approval-configurations', {
            params,
        })
        return response.data
    }

    /**
     * Get all approvals without permission requirements (basic fields)
     */
    async listAll(params?: ListApprovalsParams): Promise<PaginatedResponse<ApprovalTemplate>> {
        const response = await this.client.get<PaginatedResponse<ApprovalTemplate>>(
            '/approval-configurations/list-all',
            {
                params,
            },
        )
        return response.data
    }

    /**
     * Get a single approval by ID
     */
    async findOne(id: string): Promise<ApprovalTemplate> {
        const response = await this.client.get<ApprovalTemplate>(`/approval-configurations/${id}`)
        return response.data
    }

    /**
     * Create approval template
     */
    async create(data: CreateApprovalTemplateDto): Promise<ApprovalTemplate> {
        const response = await this.client.post<ApprovalTemplate>('/approval-configurations', data)
        return response.data
    }

    /**
     * Update approval template
     */
    async update(id: string, data: UpdateApprovalTemplateDto): Promise<ApprovalTemplate> {
        const payload = { ...data, isConfirm: data.isConfirm ?? false }
        const response = await this.client.patch<ApprovalTemplate>(`/approval-configurations/${id}`, payload)
        return response.data
    }

    /**
     * Delete approval template
     */
    async delete(id: string, isConfirm: boolean = false): Promise<void> {
        await this.client.delete(`/approval-configurations/${id}`, {
            params: { isConfirm },
        })
    }

    /**
     * Approve an approval request
     */
    async approve(id: string, payload?: ApproveRequestBody): Promise<Approval> {
        const response = await this.client.post<Approval>(`/approval-configurations/${id}/approve`, payload)
        return response.data
    }

    /**
     * Reject an approval request
     */
    async reject(id: string, payload: RejectRequestBody): Promise<Approval> {
        const response = await this.client.post<Approval>(`/approval-configurations/${id}/reject`, payload)
        return response.data
    }

    /**
     * Approve a request approval
     */
    async approveRequestApproval(id: string): Promise<Approval> {
        const response = await this.client.post<Approval>(`/request-approvals/${id}/approve`, {})
        return response.data
    }

    /**
     * Reject a request approval
     */
    async rejectRequestApproval(id: string, payload: { comment: string }): Promise<Approval> {
        const response = await this.client.post<Approval>(`/request-approvals/${id}/reject`, payload)
        return response.data
    }
}
